// apply(world, command): validate, then mutate. The only way state changes outside tick().
import type { SimEvent } from './events';
import { isBuildable, pointInPolygon } from './geometry';
import type { Balance, Command, MapData, PlantType, World, Zone, ZoneType } from './types';
import { makePlant, makeSubstation, makeTap } from './world';

export interface ApplyResult {
  ok: boolean;
  /** Present when ok is false. Short, player-readable. */
  reason?: string;
  events: SimEvent[];
}

function fail(reason: string): ApplyResult {
  return { ok: false, reason, events: [] };
}

function pay(world: World, cost: number): string | undefined {
  if (world.cash < cost) return `need $${cost}`;
  world.cash -= cost;
  return undefined;
}

/** Price of a command, or undefined when it is not a purchase. */
export function commandCost(command: Command, balance: Balance): number | undefined {
  switch (command.t) {
    case 'PlacePlant':
      return balance.plants[command.type].cost;
    case 'PlaceSubstation':
      return balance.substation.cost;
    case 'MoveSubstation':
      return balance.substation.moveFee;
    case 'UpgradeTransformer':
      return balance.substation.transformerUpgradeCost;
    case 'UpgradeLine':
      return balance.line.highVoltageCost;
    case 'AddBattery':
      return balance.battery.cost;
    default:
      return undefined;
  }
}

function zonesContaining(x: number, y: number, map: MapData): Zone[] {
  const out: Zone[] = [];
  for (const z of map.zones) {
    if (pointInPolygon(x, y, z.polygon)) out.push(z);
  }
  return out;
}

/** Why a plant of this type cannot go at (x, y), or undefined if it can. */
export function plantPlacementError(
  world: World,
  map: MapData,
  balance: Balance,
  type: PlantType,
  x: number,
  y: number,
): string | undefined {
  if (!isBuildable(x, y, map)) return 'cannot build there';
  const spec = balance.plants[type];
  const allowed: ZoneType[] = spec.zones;
  if (allowed.includes('open')) return undefined; // anywhere on the map
  const here = zonesContaining(x, y, map).filter((z) => allowed.includes(z.type));
  if (here.length === 0) return `needs a ${allowed.join(' or ')} zone`;
  for (const zone of here) {
    const slots = zone.slots ?? Infinity;
    const used = world.plants.filter(
      (p) => p.type === type && pointInPolygon(p.x, p.y, zone.polygon),
    ).length;
    if (used < slots) return undefined;
  }
  return 'no free slots in this zone';
}

export function apply(world: World, command: Command, map: MapData, balance: Balance): ApplyResult {
  const events: SimEvent[] = [];
  if (world.status === 'over' && command.t !== 'SetSpeed') return fail('game is over');

  switch (command.t) {
    case 'Pause': {
      world.status = 'paused';
      return { ok: true, events };
    }
    case 'Resume': {
      world.status = 'running';
      return { ok: true, events };
    }
    case 'SetSpeed': {
      world.speed = command.speed;
      return { ok: true, events };
    }

    case 'PlacePlant': {
      const why = plantPlacementError(world, map, balance, command.type, command.x, command.y);
      if (why) return fail(why);
      const cost = balance.plants[command.type].cost;
      const broke = pay(world, cost);
      if (broke) return fail(broke);
      const plant = makePlant(world, balance, command.type, command.x, command.y);
      world.plants.push(plant);
      events.push({ t: 'PlantBuilt', plantId: plant.id, type: plant.type });
      return { ok: true, events };
    }

    case 'PlaceSubstation': {
      if (!isBuildable(command.x, command.y, map)) return fail('cannot build there');
      const broke = pay(world, balance.substation.cost);
      if (broke) return fail(broke);
      world.substations.push(makeSubstation(world, map, balance, command.x, command.y));
      return { ok: true, events };
    }

    case 'MoveSubstation': {
      const sub = world.substations.find((s) => s.id === command.id);
      if (!sub) return fail('no such substation');
      if (!isBuildable(command.x, command.y, map)) return fail('cannot build there');
      const broke = pay(world, balance.substation.moveFee);
      if (broke) return fail(broke);
      sub.x = command.x;
      sub.y = command.y;
      return { ok: true, events };
    }

    case 'UpgradeTransformer': {
      const sub = world.substations.find((s) => s.id === command.id);
      if (!sub) return fail('no such substation');
      const broke = pay(world, balance.substation.transformerUpgradeCost);
      if (broke) return fail(broke);
      sub.capacity += balance.substation.transformerUpgradeCapacity;
      return { ok: true, events };
    }

    case 'Tap': {
      const plant = world.plants.find((p) => p.id === command.plantId);
      const sub = world.substations.find((s) => s.id === command.substationId);
      if (!plant || !sub) return fail('no such plant or substation');
      const existing = world.taps.filter((t) => t.plantId === plant.id);
      if (existing.some((t) => t.substationId === sub.id)) return fail('already connected');
      if (existing.length >= plant.maxTaps) return fail('plant has no free taps');
      world.taps.push(makeTap(world, plant.id, sub.id));
      return { ok: true, events };
    }

    case 'Untap': {
      const i = world.taps.findIndex((t) => t.id === command.tapId);
      if (i < 0) return fail('no such line');
      world.taps.splice(i, 1);
      return { ok: true, events };
    }

    case 'UpgradeLine': {
      const tap = world.taps.find((t) => t.id === command.tapId);
      if (!tap) return fail('no such line');
      if (tap.highVoltage) return fail('already high voltage');
      const broke = pay(world, balance.line.highVoltageCost);
      if (broke) return fail(broke);
      tap.highVoltage = true;
      return { ok: true, events };
    }

    case 'AddBattery': {
      return fail('batteries arrive in slice 3');
    }
  }
}
